import React from 'react';
import { QuillDeltaToHtmlConverter } from 'quill-delta-to-html';
import { parseRichTextPayload } from '@/lib/richText';
import { processMessageText } from '@/lib/utils';
import styles from './CenterPanel.module.css';

interface RichTextDisplayProps {
    /** The raw message payload string, potentially a [RICH_TEXT:...] envelope */
    payload: string;
    /** Optional additional CSS class name */
    className?: string;
}

/**
 * Renders a rich text payload as formatted HTML.
 * Parses the [RICH_TEXT:...] envelope, converts the Quill Delta to semantic HTML,
 * and renders it inline within the message bubble.
 *
 * Falls back to standard processMessageText rendering if the payload
 * does not match the rich text pattern.
 */
export default function RichTextDisplay({ payload, className = '' }: RichTextDisplayProps) {
    const parsed = parseRichTextPayload(payload);

    if (!parsed) {
        // Not a rich text payload — fall back to standard text rendering
        return (
            <span dangerouslySetInnerHTML={{ __html: processMessageText(payload) }} />
        );
    }

    // Configure the Quill Delta → HTML converter
    const converterOptions = {
        inlineStyles: true,       // Output inline style attributes (e.g. colors) instead of Quill CSS classes
        multiLineParagraph: true,  // Wrap multi-line content in <p> tags
    };

    const converter = new QuillDeltaToHtmlConverter(parsed.delta, converterOptions);
    const htmlContent = converter.convert();

    return (
        <div
            className={`${styles.richTextContent} ${className}`}
            dangerouslySetInnerHTML={{ __html: htmlContent }}
        />
    );
}
